/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import ArgosMenuItem from "../../../../../components/Form/Select/ArgosMenuItem";
import ArgosSelect from "../../../../../components/Form/Select/ArgosSelect";
import {ArgosSelectOption} from "../../../../../components/Form/Select/types";
import {createReportHorizontalTabs} from "../../../../../components/Reports/ReportView";
import FormControl from "@mui/material/FormControl";
import {getReportOptionsEndpoint, getViolationsReportEndpoint, idToLabelModalitiesMap} from "./constants";
import {HorizontalTabs} from "../../../../../components/Tabs/HorizontalTabs";
import InputLabel from "@mui/material/InputLabel";
import Loader from "../../../../../components/Loader/Loader";
import React, {useEffect, useMemo, useState} from "react";
import {selectDialogState} from "../../../../../../redux/ducks/VehicleProgramsDetails/slice";
import toast from "react-hot-toast";
import Typography from "@mui/material/Typography";
import {useAppSelector} from "../../../../../../redux/hooks";
import useGetUrlParams from "../../../../../../hooks/useGetUrlParams";
import {usePostRequest} from "../../../../../../api/helper"
import {useSearchParams} from "react-router-dom";
import {EReportTypes} from "../../../../../components/Reports/constants";
import {ReportPages} from "../../../../AdasPrograms/ItemDetails/common/constants";


const ReportView = React.memo((props: any) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlParams = Object.fromEntries(searchParams)
  const paramobj: any = useAppSelector(state => {
    const dialogState = selectDialogState(state)
    if (dialogState.isReportDialogOpen) {
      return dialogState.component?.params ?? urlParams
    } else {
      return {
        adasReportId: urlParams.adasReportId,
      }
    }
  })
  const {developmentStageId, vehicleProgramId, adasProgramId} = useGetUrlParams()

  const [adasReportId, setAdasReportId] = useState<string>(paramobj?.adasReportId ?? "")
  const [reportOptions, setReportOptions] = useState<any[]>([])
  const [selectedReport, setSelectedReport] = useState<string>("")
  const [innerHorizontalTabs, setInnerHorizontalTabs] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  useEffect(() => {
    handleGetReportOptions()
  }, [vehicleProgramId, developmentStageId, adasReportId, adasProgramId])

  useEffect(() => {
    if (selectedReport !== "") {
      handleGetViolationsReport()
    }
  }, [selectedReport])

  const getReportOptions = usePostRequest(getReportOptionsEndpoint)
  const handleGetReportOptions = () => {
    if (!vehicleProgramId || !developmentStageId || !adasReportId || !adasProgramId) {
      return
    }
    const reqBody = {
      vehicleProgramId,
      developmentStageId,
      adasReportId,
      adasProgramId
    }
    setIsLoading(true)
    getReportOptions(reqBody).then((res: any) => {
      setReportOptions(res)
    }).catch((err: any) => {
      console.log(err)
      toast.error("An error occurred while fetching the available reports options.")
    }).finally(() => {
      setIsLoading(false)
    })
  }

  const getViolationsReport = usePostRequest(getViolationsReportEndpoint)
  const handleGetViolationsReport = () => {
    const reqBody = {
      "reportId": selectedReport,
    }
    setIsLoading(true)
    getViolationsReport(reqBody).then((res: any) => {
      setInnerHorizontalTabs(createReportHorizontalTabs(res, ReportPages.SENSING_SYSTEM_PERFORMANCE))
    }).catch((err: any) => {
      console.log(err)
      toast.error("An error occurred while fetching the requested report")
    }).finally(() => {
      setIsLoading(false)
    })
  }

  const contentBelowStaticTabName = useMemo(() => {
    return (
      <></>
    )
  }, [selectedReport])

  const contentBelowTabNames = useMemo(() => {
    return (
      {[EReportTypes.STATIC_REPORT]: contentBelowStaticTabName}
    )
  }, [contentBelowStaticTabName])

  return (
    <ArgosFlexbox vertical style={{height: "100%", padding: "20px"}}>
      <FormControl variant="standard">
        <InputLabel id="select-report">Select Report</InputLabel>
        <ArgosSelect
          required
          label="Select Report"
          type="text"
          value={selectedReport}
          id="select-report"
          onChange={(e) => setSelectedReport(e.target.value as string)}
        >
          {reportOptions?.map((modality: ArgosSelectOption) =>
            <ArgosMenuItem key={modality.id} value={modality.id}>
              {idToLabelModalitiesMap?.[modality.label] ?? modality.label}
            </ArgosMenuItem>
          )}
        </ArgosSelect>
      </FormControl>
      {selectedReport === "" && <Typography>Please select a report to view</Typography>}
      {selectedReport !== "" && <ArgosFlexbox vertical style={{height: "100%"}}>
        <Loader isLoading={isLoading}>
          <div style={{flex: 1, overflow: 'auto', scrollbarGutter: 'stable'}}>
            <HorizontalTabs
              padding={{padding: '0px 10px 10px 10px'}}
              initContent={null}
              initHtabIndex={0}
              horizontalTabs={innerHorizontalTabs}
              contentBelowTabNames={contentBelowTabNames}
              align={'left'}
              uid={'ihtabs-1'}
            />
          </div>
        </Loader>
      </ArgosFlexbox>}
    </ArgosFlexbox>
  )
})

export default ReportView
